import { useEffect, useMemo, useRef, useState } from 'react';

import { useSmartCardContext } from '@atlaskit/link-provider';
import { getStatus } from '@atlaskit/linking-common';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UnauthProvider {
	/** Provider key for deduplication (e.g. "figma") */
	key: string;
	/** Provider display name (e.g. "Figma") */
	name: string;
	/** Number of unauthenticated links from this provider on the page */
	linkCount: number;
	/** OAuth URL to initiate the connect flow */
	authUrl: string;
	/**
	 * List of link titles for this provider's unauthenticated links.
	 * Priority: data.name (document title) → data.url (ORS url) → raw URL (store key)
	 */
	links: string[];
}

export interface UseUnauthenticatedLinkDataResult {
	/** Whether the store is still settling (cards still resolving) */
	isLoading: boolean;
	/** The provider with the most unauthenticated links, or null if none */
	topProvider: UnauthProvider | null;
	/** All unauthenticated providers on the page, sorted by link count descending */
	allProviders: UnauthProvider[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** How long to wait after all cards settle before finalizing results */
const SETTLE_DEBOUNCE_MS = 500;

/**
 * Hard timeout before we give up waiting for cards to resolve.
 * On timeout we finalize with whatever is in the store — if the store
 * is empty we return no providers (safe: nudge will not show).
 */
const HARD_TIMEOUT_MS = 5000;

/**
 * Regex to strip known Atlassian auth display name prefixes, covering all 3 patterns:
 *   "Atlassian Links - X"     (most common)
 *   "Atlassian - X"           (GitHub, Google Drive, GCP, Gmail, Notion, SharePoint)
 *   "Atlassian Connector - X" (Slack)
 */
const DISPLAY_NAME_PREFIX_REGEX = /^Atlassian(?:\s+(?:Links|Connector))?\s*-\s*/i;

/**
 * Strips the Atlassian display name prefix from a provider display name.
 * Defined outside loops to satisfy @atlassian/perf-linting/no-expensive-split-replace.
 */
const stripDisplayNamePrefix = (displayName: string): string =>
	displayName.replace(DISPLAY_NAME_PREFIX_REGEX, '').trim();

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Queries the DOM for all smart link URLs currently rendered on the page.
 * Used to filter the store to only URLs visible in the current page context,
 * avoiding stale entries from previous navigations.
 */
const getPageSmartLinkUrls = (): Set<string> => {
	const elements = document.querySelectorAll<HTMLAnchorElement>(
		'[data-inline-card="true"], [data-card-appearance="inline"], [data-card-appearance="block"]',
	);
	const urls = new Set<string>();
	elements.forEach((el) => {
		const href = el.getAttribute('href') || el.getAttribute('data-card-url');
		if (href) {
			urls.add(href);
		}
	});
	return urls;
};

/**
 * Scans a CardStore state object for unauthenticated entries, groups them by
 * provider, and returns all providers sorted by link count descending.
 *
 * @param storeState - The raw CardStore state (URL → card entry map)
 * @param pageUrls   - Optional allowlist of URLs present on the current page.
 *                     When provided, only entries whose URL is in this set are considered.
 *                     This prevents stale store entries from previous pages from showing up.
 */
export const scanForProviders = (
	storeState: Record<string, any>,
	pageUrls?: Set<string>,
): UnauthProvider[] => {
	const urls = Object.keys(storeState).filter((url) => !pageUrls || pageUrls.has(url));
	const providerMap: Record<string, UnauthProvider> = {};

	urls.forEach((url) => {
		const card = storeState[url];
		if (card?.status === 'unauthorized' && card?.details?.meta?.auth?.length) {
			const authEntry = card.details.meta.auth[0];
			const providerKey = authEntry.key || 'unknown';

			// Use the auth displayName as the source of truth for the provider name.
			// generator.name reflects the Forge app's internal name (e.g. "Figma Forge")
			// which is not user-friendly — the auth displayName is the correct source.
			const rawDisplayName: string = authEntry.displayName || providerKey;
			const providerName = stripDisplayNamePrefix(rawDisplayName);
			const authUrl = authEntry.url;

			// Link title fallback chain:
			// 1. data.name — actual document/file title from ORS (best)
			// 2. data.url  — URL from ORS response (fallback)
			// 3. url       — raw store key (last resort)
			const linkTitle: string = card.details?.data?.name || card.details?.data?.url || url;

			if (!providerMap[providerKey]) {
				providerMap[providerKey] = {
					key: providerKey,
					name: providerName,
					linkCount: 0,
					authUrl,
					links: [],
				};
			}
			providerMap[providerKey].linkCount++;
			providerMap[providerKey].links.push(linkTitle);
		}
	});

	return Object.values(providerMap).sort((a, b) => b.linkCount - a.linkCount);
};

/**
 * Reads SSR smartlink data directly from window variables, bypassing the
 * SmartCard store. Allows detecting unauthenticated links on first render
 * before the store has initialised.
 */
const scanSSRData = (): UnauthProvider[] => {
	if (typeof window === 'undefined') {
		return [];
	}

	// View page SSR data — already in CardStore format
	const viewPageSSRData = (window as any)['__SSR_SMARTLINKS__'];
	if (viewPageSSRData && Object.keys(viewPageSSRData).length > 0) {
		return scanForProviders(viewPageSSRData);
	}

	// Editor SSR data — needs converting to CardStore format
	const editorSSRData = (window as any)['__SSR_EDITOR_NODE_DATA_PROVIDERS_DATA__'];
	const cardProviderData = editorSSRData?.editorCardProvider;
	if (cardProviderData && Object.keys(cardProviderData).length > 0) {
		const storeFormat: Record<string, any> = {};
		Object.entries(cardProviderData).forEach(([url, details]: [string, any]) => {
			storeFormat[url] = { status: getStatus(details), details };
		});
		return scanForProviders(storeFormat);
	}

	return [];
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns data about unauthenticated (unconnected) smart link providers on the current page.
 *
 * This hook is a **data layer only** — it answers "which providers have unconnected smart links
 * on this page and how many?". It does NOT check view mode, scroll position, or dismissal state.
 * Those eligibility checks are the responsibility of the consuming component (SmartlinkAuthNudge).
 *
 * Uses a triple-path approach to ensure data is available as early as possible:
 * 1. SSR window data scan — synchronous, fastest, available before store init
 * 2. Store synchronous scan — works if store was pre-populated with SSR data
 * 3. Store subscription — waits for cards to settle on the client (500ms debounce, 5s timeout)
 *
 * @param isEnabled - Whether the experiment is enabled. Pass `expValEquals('cc_smart_link_nudge_kill_switch', 'isEnabled', true)`
 *   at the callsite. Defaults to `true` for convenience in tests/isolation. When `false`, returns empty result immediately
 *   with no store scanning or subscriptions.
 *
 * Error handling: any error in store scanning is caught and treated as
 * "no unauthenticated links" to prevent erroneously showing the nudge.
 */
export function useUnauthenticatedSmartLinkProviders(
	pageId?: string,
	isEnabled: boolean = true,
): UseUnauthenticatedLinkDataResult {
	return useUnauthenticatedSmartLinkProvidersInner(pageId, isEnabled);
}

const EMPTY_RESULT: UseUnauthenticatedLinkDataResult = {
	isLoading: false,
	topProvider: null,
	allProviders: [],
};

function useUnauthenticatedSmartLinkProvidersInner(
	pageId?: string,
	isEnabled: boolean = true,
): UseUnauthenticatedLinkDataResult {
	// useSmartCardContext safely returns undefined when not inside a SmartCardProvider
	// (unlike useSmartLinkContext which throws). We guard on value below.
	const { value: cardContext } = useSmartCardContext();
	const store = cardContext?.store;

	// Synchronous initial scan — SSR data first, then store
	const initialProviders = useMemo(() => {
		// Return empty immediately if experiment is disabled
		if (!isEnabled) {
			return [];
		}
		try {
			// Path 1: SSR window data (fastest — available before store init)
			const ssrResult = scanSSRData();
			if (ssrResult.length > 0) {
				return ssrResult;
			}

			// Path 2: Store synchronous read (works if store was pre-populated)
			if (store) {
				return scanForProviders(store.getState());
			}
		} catch {
			// Error scanning — default to empty (do not show nudge)
		}
		return [];
	}, [isEnabled, store]);

	const [allProviders, setAllProviders] = useState<UnauthProvider[]>(initialProviders);
	const [isLoading, setIsLoading] = useState(initialProviders.length === 0);
	const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const hardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const hasFinalized = useRef(initialProviders.length > 0);

	// Reset state when pageId changes (SPA page navigation)
	// The SmartCard store is global and persists across page navigations,
	// so we must re-scan when the page context changes to avoid showing
	// stale providers from the previous page.
	useEffect(() => {
		if (!pageId) {
			return;
		}
		hasFinalized.current = false;
		setAllProviders([]);
		setIsLoading(true);
	}, [pageId]);

	// Path 3: Store subscription — for when SSR data isn't available
	useEffect(() => {
		if (!isEnabled || !store || hasFinalized.current) {
			return;
		}

		const finalize = () => {
			if (hasFinalized.current) {
				return;
			}
			hasFinalized.current = true;
			if (settleTimerRef.current) {
				clearTimeout(settleTimerRef.current);
			}
			if (hardTimerRef.current) {
				clearTimeout(hardTimerRef.current);
			}
			try {
				const storeState = store.getState();
				const result = scanForProviders(storeState, getPageSmartLinkUrls());
				setAllProviders(result);
			} catch {
				// Error reading store or scanning — default to empty (do not show nudge)
				setAllProviders([]);
			} finally {
				setIsLoading(false);
			}
		};

		const checkSettled = () => {
			try {
				const state = store.getState();
				const urls = Object.keys(state);

				if (urls.length === 0) {
					return;
				}

				const hasPending = urls.some((url) => {
					const status = state[url]?.status;
					return status === 'pending' || status === 'resolving';
				});

				if (!hasPending) {
					if (settleTimerRef.current) {
						clearTimeout(settleTimerRef.current);
					}
					settleTimerRef.current = setTimeout(finalize, SETTLE_DEBOUNCE_MS);
				}
			} catch {
				// Error reading store — finalize with empty result (do not show nudge)
				finalize();
			}
		};

		hardTimerRef.current = setTimeout(finalize, HARD_TIMEOUT_MS);

		const unsubscribe = store.subscribe(() => {
			if (!hasFinalized.current) {
				checkSettled();
			}
		});

		// Run immediately in case store already has data
		checkSettled();

		return () => {
			if (settleTimerRef.current) {
				clearTimeout(settleTimerRef.current);
			}
			if (hardTimerRef.current) {
				clearTimeout(hardTimerRef.current);
			}
			unsubscribe();
		};
	}, [isEnabled, store]);

	if (!isEnabled) {
		return EMPTY_RESULT;
	}

	return {
		isLoading,
		topProvider: allProviders[0] ?? null,
		allProviders,
	};
}
